import { useQuery } from '@tanstack/react-query'

import makeGetStrings from './lib/makeGetStrings'
import retry from './lib/retry'


export default function useLocaleStringsFile({ isFirstPaintGated, locale: originalLocale, urlTemplate }) {
  const locale = originalLocale.toLowerCase()

  // The request carries no timeout of its own. A gated caller bounds its
  // own wait, so one long enough not to cut off a slow-but-live response
  // would outlast the wait it was meant to bound anyway, while cutting
  // the response off denies the real strings to every tree that mounts
  // later.
  const getStrings = makeGetStrings({
    locale,
    urlTemplate,
  })

  // A gated caller is holding a frame back until this query settles, so
  // what it needs is an answer, not necessarily a successful one.
  // Retrying can't make such a caller wait any longer, but it can stop it
  // from giving up early, because a query that is between attempts hasn't
  // settled. An immediate failure is an answer worth having: offline is
  // precisely when the caller most needs to stop waiting and paint
  // something.
  const retryPolicy = ( isFirstPaintGated ) ? false : retry

  // React Query pauses a query instead of running it when the browser
  // reports itself offline, and a paused query never settles, so a gated
  // caller would wait for connectivity to come back. Such a caller runs
  // the request regardless and lets it fail fast. Callers that aren't
  // gated keep the pausing behavior, which eventually gets them their
  // translations.
  const networkMode = ( isFirstPaintGated ) ? 'always' : void 0

  // Every provider on the page shares this one query, so a second one
  // mounting is enough to fetch these strings all over again: a query
  // that has only ever failed counts as stale, which makes it eligible
  // both when an observer mounts and when the browser reconnects. Neither
  // can reach a tree that already painted, because it holds the copy it
  // painted with, so for a gated caller they are requests whose result
  // has nowhere to go.
  const refetchAfterFailure = ( isFirstPaintGated ) ? false : void 0

  const {
    data = {},
    ...query
  } = useQuery({
    networkMode,
    queryFn: getStrings,
    queryKey: ['useLocaleStringsFile', urlTemplate, locale],
    refetchOnReconnect: refetchAfterFailure,
    refetchOnWindowFocus: false,
    retry: retryPolicy,
    retryOnMount: refetchAfterFailure,
    staleTime: Infinity,
  })

  return {
    ...query,
    localeStrings: data.data,
  }
}
